import React, { useCallback, useState } from 'react';
import { Modal, Button } from '../components/v2';

/**
 * Promise-based confirmation using the platform Modal — the single reusable
 * replacement for the browser's native window.confirm (which is unstyled,
 * un-brandable, blocks the event loop, and is suppressible by the browser).
 *
 * Usage:
 *   const { confirm, confirmModal } = useConfirm();
 *   // ...render {confirmModal} once in the component tree...
 *   const onDelete = async () => {
 *     if (!(await confirm({ title: 'Delete X?', message: '…', confirmLabel: 'Delete' }))) return;
 *     // proceed
 *   };
 *
 * confirm() resolves true on confirm, false on cancel/backdrop/escape. Danger
 * styling is the default (these are destructive confirms); pass danger:false for
 * a neutral confirm.
 */
export function useConfirm() {
  const [state, setState] = useState(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    setState((prev) => {
      // If a confirm is somehow already open, resolve it false before replacing
      // so its awaiter never hangs.
      if (prev && prev.resolve) prev.resolve(false);
      return {
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        danger: opts.danger !== false,
        resolve,
      };
    });
  }), []);

  const settle = useCallback((result) => {
    setState((prev) => { if (prev && prev.resolve) prev.resolve(result); return null; });
  }, []);

  const confirmModal = (
    <Modal
      isOpen={!!state}
      onClose={() => settle(false)}
      title={state?.title}
      ariaLabel={typeof state?.title === 'string' ? state.title : 'Confirmation'}
      size="sm"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => settle(false)}>{state?.cancelLabel || 'Cancel'}</Button>
          <Button variant={state?.danger ? 'danger' : 'primary'} onClick={() => settle(true)}>
            {state?.confirmLabel || 'Confirm'}
          </Button>
        </div>
      )}
    >
      <div className="text-[12.5px] text-v2-ink leading-relaxed">{state?.message}</div>
    </Modal>
  );

  return { confirm, confirmModal };
}
